// frontend/src/services/websocketService.js
// Servicio WebSocket completo para Arsenal TPMS

class WebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.connectionId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    this.heartbeatInterval = null;
    this.messageHandlers = new Map();
    this.pendingSubscriptions = [];
    this.subscribedVehicles = new Set();
    this.subscribedChannels = new Set();
    this.token = null;
    
    // Event emitter simple
    this.listeners = {};
    
    // Debug counter
    this.messageCount = 0;
  }

  // ==================== CONNECTION ====================

  connect(token) {
    if (!token) {
      console.warn('WebSocket: No token provided');
      return;
    }
    
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.token = token;
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/ws?token=${encodeURIComponent(token)}`;

    try {
      this.ws = new WebSocket(wsUrl);
      this._setupEventHandlers();
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this._scheduleReconnect();
    }
  }

  _setupEventHandlers() {
    this.ws.onopen = () => {
      console.log('🔌 WS onopen - conectado');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;

      this._processPendingSubscriptions();

      if (this.subscribedVehicles.size > 0) {
        console.log('🔄 WS onopen - re-suscribiendo vehículos previos:', Array.from(this.subscribedVehicles));
        const vehicleIds = Array.from(this.subscribedVehicles);
        this.subscribedVehicles.clear();
        this.subscribeToVehicles(vehicleIds);
      }

      this._startHeartbeat();
      this._emit('connected', { connectionId: this.connectionId });
    };

    this.ws.onmessage = (event) => {
      this.messageCount++;
      try {
        const message = JSON.parse(event.data);
        // Log ALL messages except heartbeats to diagnose what arrives
        if (message.type !== 'heartbeat' && message.type !== 'heartbeat_ack') {
          console.log(`🌐 WS MSG [${this.messageCount}] type="${message.type}"`, message.data || message);
        }
        this._handleMessage(message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error, event.data);
      }
    };

    this.ws.onclose = (event) => {
      this.isConnected = false;
      this.connectionId = null;
      this._stopHeartbeat();
      
      this._emit('disconnected', { code: event.code, reason: event.reason });
      
      if (event.code !== 1000 && event.code !== 4001 && event.code !== 4002) {
        if (this.token) {
          this._scheduleReconnect();
        }
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this._emit('error', { error });
    };
  }

  _scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this._emit('max_reconnect_reached', {});
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    setTimeout(() => {
      if (this.token) {
        this.connect(this.token);
      }
    }, delay);
  }

  disconnect() {
    this._stopHeartbeat();
    this.token = null;
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.isConnected = false;
    this.connectionId = null;
    this.subscribedVehicles.clear();
    this.subscribedChannels.clear();
  }

  // ==================== HEARTBEAT ====================

  _startHeartbeat() {
    this._stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'heartbeat' });
      }
    }, 25000);
  }

  _stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // ==================== MESSAGE HANDLING ====================

  _handleMessage(message) {
    const { type, data } = message;

    switch (type) {
      case 'connection_established':
        this.connectionId = data.connection_id;
        break;

      case 'heartbeat':
      case 'heartbeat_ack':
        break;

      case 'subscription_confirmed':
        console.log('✅ WS: Suscripción confirmada por servidor:', data);
        break;

      case 'telemetry_update':
        this._emit('telemetry_update', data);
        break;
        
      case 'telemetry_batch':
        this._emit('telemetry_batch', data);
        break;
        
      case 'vehicle_data_update':
        this._emit('vehicle_data_update', data);
        break;

      case 'alarm_triggered':
        console.warn('🚨 ALARM:', data);
        this._emit('alarm_triggered', data);
        break;
        
      case 'alarm_cleared':
        this._emit('alarm_cleared', data);
        break;
        
      case 'alarm_status_update':
      case 'alarm_status_batch':
        this._emit('alarm_status_update', data);
        break;

      case 'ticket_created':
        console.warn('🎫 NEW TICKET:', data);
        this._emit('ticket_created', data);
        break;
        
      case 'ticket_updated':
        this._emit('ticket_updated', data);
        break;
        
      case 'ticket_acknowledged':
        this._emit('ticket_acknowledged', data);
        break;
        
      case 'ticket_resolved':
        this._emit('ticket_resolved', data);
        break;
        
      case 'active_tickets_update':
        this._emit('active_tickets_update', data);
        break;

      case 'vehicle_online':
        this._emit('vehicle_online', data);
        break;
        
      case 'vehicle_offline':
        this._emit('vehicle_offline', data);
        break;

      case 'sync_status':
        this._emit('sync_status', data);
        break;
        
      case 'sync_complete':
        this._emit('sync_complete', data);
        break;

      case 'chat_message':
        this._emit('chat_message', data);
        break;

      case 'chat_channel_deleted':
        this._emit('chat_channel_deleted', data);
        break;

      case 'error':
        console.error('WS Error:', data);
        this._emit('ws_error', data);
        break;

      default:
        this._emit(type, data);
    }
  }

  // ==================== SUBSCRIPTIONS ====================

  subscribeToVehicle(vehicleId) {
    if (!vehicleId) return;
    
    const vid = String(vehicleId);
    
    if (this.subscribedVehicles.has(vid)) {
      return;
    }
    
    if (this.isConnected) {
      this.send({
        type: 'subscribe',
        channel: `vehicle:${vid}`
      });
      this.subscribedVehicles.add(vid);
    } else {
      this.pendingSubscriptions.push({ type: 'vehicle', id: vid });
    }
  }

  subscribeToVehicles(vehicleIds) {
    if (!Array.isArray(vehicleIds) || vehicleIds.length === 0) return;

    const newVehicles = vehicleIds
      .map(String)
      .filter(id => !this.subscribedVehicles.has(id));

    if (newVehicles.length === 0) {
      console.log('🔕 subscribeToVehicles: ya suscritos, no se envía mensaje', vehicleIds);
      return;
    }

    if (this.isConnected) {
      const msg = { type: 'subscribe_vehicles', vehicle_ids: newVehicles };
      console.log('📤 Enviando suscripción al servidor:', msg);
      this.send(msg);
      newVehicles.forEach(id => this.subscribedVehicles.add(id));
    } else {
      console.warn('📤 WS no conectado, agregando a pendingSubscriptions:', newVehicles);
      newVehicles.forEach(id => {
        this.pendingSubscriptions.push({ type: 'vehicle', id });
      });
    }
  }

  unsubscribeFromVehicle(vehicleId) {
    const vid = String(vehicleId);
    
    if (this.isConnected && this.subscribedVehicles.has(vid)) {
      this.send({
        type: 'unsubscribe',
        channel: `vehicle:${vid}`
      });
    }
    
    this.subscribedVehicles.delete(vid);
  }

  subscribeToChannel(channel) {
    if (this.subscribedChannels.has(channel)) return;
    
    if (this.isConnected) {
      this.send({
        type: 'subscribe',
        channel
      });
      this.subscribedChannels.add(channel);
    } else {
      this.pendingSubscriptions.push({ type: 'channel', id: channel });
    }
  }

  _processPendingSubscriptions() {
    const vehicleIds = this.pendingSubscriptions
      .filter(s => s.type === 'vehicle')
      .map(s => s.id);
    
    const channels = this.pendingSubscriptions
      .filter(s => s.type === 'channel')
      .map(s => s.id);
    
    if (vehicleIds.length > 0) {
      this.subscribeToVehicles(vehicleIds);
    }
    
    channels.forEach(channel => {
      this.subscribeToChannel(channel);
    });
    
    this.pendingSubscriptions = [];
  }

  // ==================== SENDING ====================

  send(data) {
    if (!this.isConnected || !this.ws) {
      console.warn('WebSocket not connected, cannot send:', data);
      return false;
    }
    
    try {
      this.ws.send(JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Failed to send WebSocket message:', error);
      return false;
    }
  }

  // ==================== EVENT EMITTER ====================

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    
    return () => {
      this.off(event, callback);
    };
  }

  off(event, callback) {
    if (!this.listeners[event]) return;
    
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }

  _emit(event, data) {
    if (!this.listeners[event]) return;
    
    this.listeners[event].forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in ${event} listener:`, error);
      }
    });
  }

  // ==================== STATUS ====================

  getStatus() {
    return {
      isConnected: this.isConnected,
      connectionId: this.connectionId,
      reconnectAttempts: this.reconnectAttempts,
      subscribedVehicles: Array.from(this.subscribedVehicles),
      subscribedChannels: Array.from(this.subscribedChannels),
      messageCount: this.messageCount,
      registeredEvents: Object.keys(this.listeners)
    };
  }
}

const websocketService = new WebSocketService();
export default websocketService;